/**
 * QuickCategoryCreateDialog
 * Allows creating a new category or subcategory on-the-fly during Portal import
 * Simplified version of CategoryManagement's create dialog
 */

import React, { useState } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Upload, X, AlertCircle } from 'lucide-react';
import {
  organizationCategoryService,
  type DomainType,
  type CreateCategoryDto,
} from '@/features/product-library/services/organizationCategoryService';
import {
  CategoryIcon,
  validateSVG,
  type CategoryIconConfig,
} from '@/features/product-library/components/CategoryIcon';
import type { MergedCategory } from '@/features/product-library/hooks/useMergedCategories';
import {
  normalizeSvgForDynamicColor,
  detectSvgBackground,
  removeSvgBackground,
} from '@/lib/svgNormalizer';
import { sanitizeSVG } from '@/lib/sanitize';

interface QuickCategoryCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  domain: DomainType;
  onCategoryCreated: (category: MergedCategory) => void;
  isSubcategory?: boolean;
  parentCategory?: string;
  parentCategories?: MergedCategory[];
}

interface CategoryFormData {
  category: string;
  subcategory: string;
  display_name: string;
  abbreviation: string;
  fill_color: string;
  border_color: string;
  text_color: string;
  icon_shape: 'square' | 'circle';
  icon_type: 'abbreviation' | 'svg';
  icon_svg: string | null;
}

const DEFAULT_FORM_DATA: CategoryFormData = {
  category: '',
  subcategory: '',
  display_name: '',
  abbreviation: '',
  fill_color: '#6B7280',
  border_color: '#374151',
  text_color: '#FFFFFF',
  icon_shape: 'square',
  icon_type: 'abbreviation',
  icon_svg: null,
};

const COLOR_PRESETS = [
  { name: 'Gray', fill: '#6B7280', border: '#374151' },
  { name: 'Blue', fill: '#3B82F6', border: '#1D4ED8' },
  { name: 'Green', fill: '#22C55E', border: '#15803D' },
  { name: 'Yellow', fill: '#EAB308', border: '#A16207' },
  { name: 'Orange', fill: '#F97316', border: '#C2410C' },
  { name: 'Red', fill: '#EF4444', border: '#B91C1C' },
  { name: 'Purple', fill: '#A855F7', border: '#7C3AED' },
  { name: 'Pink', fill: '#EC4899', border: '#BE185D' },
  { name: 'Teal', fill: '#14B8A6', border: '#0D9488' },
];

// Helper to constrain SVG for preview display
function constrainSvgForPreview(svg: string, size: number): string {
  if (!svg) return '';
  let constrained = svg
    .replace(/\s+width\s*=\s*["'][^"']*["']/gi, '')
    .replace(/\s+height\s*=\s*["'][^"']*["']/gi, '');
  constrained = constrained.replace(
    /<svg/i,
    `<svg width="${size}" height="${size}" style="max-width:${size}px;max-height:${size}px;display:block;"`,
  );
  return constrained;
}

export function QuickCategoryCreateDialog({
  open,
  onOpenChange,
  domain,
  onCategoryCreated,
  isSubcategory = false,
  parentCategory,
  parentCategories = [],
}: QuickCategoryCreateDialogProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<CategoryFormData>({
    ...DEFAULT_FORM_DATA,
    category: parentCategory || '',
  });
  const [svgValidationWarnings, setSvgValidationWarnings] = useState<string[]>([]);

  // Background detection state
  const [originalSvg, setOriginalSvg] = useState<string | null>(null);
  const [backgroundRemovedSvg, setBackgroundRemovedSvg] = useState<string | null>(null);
  const [hasDetectedBackground, setHasDetectedBackground] = useState(false);
  const [useBackgroundRemoved, setUseBackgroundRemoved] = useState(true);
  const [detectionReason, setDetectionReason] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      setFormData({
        ...DEFAULT_FORM_DATA,
        category: parentCategory || '',
      });
      setSvgValidationWarnings([]);
      // Reset background detection state
      setOriginalSvg(null);
      setBackgroundRemovedSvg(null);
      setHasDetectedBackground(false);
      setDetectionReason('');
      setIsDragging(false);
    }
  }, [open, parentCategory]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateCategoryDto) => organizationCategoryService.createCategory(data),
    onSuccess: createdCategory => {
      // Invalidate queries to refresh category lists
      queryClient.invalidateQueries({ queryKey: ['merged-categories'] });
      queryClient.invalidateQueries({ queryKey: ['category-templates'] });

      toast.success(`${isSubcategory ? 'Subcategory' : 'Category'} created successfully`);

      // Convert to MergedCategory format for callback
      const mergedCategory: MergedCategory = {
        id: createdCategory.id,
        domain: createdCategory.domain as DomainType,
        category: createdCategory.category,
        subcategory: createdCategory.subcategory,
        display_name: createdCategory.display_name,
        icon_type: createdCategory.icon_type,
        icon_svg: createdCategory.icon_svg,
        abbreviation: createdCategory.abbreviation,
        icon_shape: createdCategory.icon_shape,
        fill_color: createdCategory.fill_color,
        border_color: createdCategory.border_color,
        text_color: createdCategory.text_color,
        display_order: createdCategory.display_order,
        is_system: false,
        is_active: createdCategory.is_active,
        org_category_id: createdCategory.id,
      };

      onCategoryCreated(mergedCategory);
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(
        `Failed to create ${isSubcategory ? 'subcategory' : 'category'}: ${error.message}`,
      );
    },
  });

  // Handle create
  const handleCreate = () => {
    if (isSubcategory) {
      if (!formData.category.trim()) {
        toast.error('Please select a parent category');
        return;
      }
      if (!formData.subcategory.trim()) {
        toast.error('Subcategory name is required');
        return;
      }
    } else {
      if (!formData.category.trim()) {
        toast.error('Category name is required');
        return;
      }
    }

    createMutation.mutate({
      domain,
      category: formData.category.trim(),
      subcategory: isSubcategory ? formData.subcategory.trim() : undefined,
      display_name: isSubcategory
        ? formData.display_name.trim() || formData.subcategory.trim()
        : formData.display_name.trim() || formData.category.trim(),
      abbreviation: formData.abbreviation.trim() || undefined,
      fill_color: formData.fill_color,
      border_color: formData.border_color,
      text_color: formData.text_color,
      icon_shape: formData.icon_shape,
      icon_type: formData.icon_type,
      icon_svg: formData.icon_type === 'svg' ? formData.icon_svg : undefined,
    });
  };

  // Helper to clear background detection state
  const clearBackgroundDetection = () => {
    setOriginalSvg(null);
    setBackgroundRemovedSvg(null);
    setHasDetectedBackground(false);
    setDetectionReason('');
  };

  // Handle background version selection
  const handleBackgroundChoice = (useRemoved: boolean) => {
    setUseBackgroundRemoved(useRemoved);
    const chosenSvg = useRemoved ? backgroundRemovedSvg : originalSvg;
    if (chosenSvg) {
      setFormData(prev => ({ ...prev, icon_svg: chosenSvg }));
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      processUploadedFile(file);
    }
  };

  // Process uploaded SVG file
  const processUploadedFile = async (file: File) => {
    if (!file.type.includes('svg') && !file.name.endsWith('.svg')) {
      toast.error('Please upload an SVG file');
      return;
    }

    try {
      const rawContent = await file.text();

      // First validate the basic SVG structure
      const validation = validateSVG(rawContent);
      if (!validation.valid) {
        toast.error(validation.error || 'Invalid SVG file');
        return;
      }

      setSvgValidationWarnings(validation.warnings);

      // Normalize the SVG for dynamic color support
      const result = normalizeSvgForDynamicColor(validation.resizedSvg || rawContent);
      const normalized = result.svg;

      if (!result.normalized && result.message) {
        toast.warning(
          'Icon uploaded but may not support dynamic colors. It will display with its original colors.',
          { duration: 5000 },
        );
      }

      // Check for background in the normalized SVG
      const detection = detectSvgBackground(normalized);

      if (detection.hasBackground && detection.confidence !== 'low') {
        // Background detected - show comparison UI
        const removed = removeSvgBackground(normalized);
        const removedNormalized = normalizeSvgForDynamicColor(removed).svg;

        setOriginalSvg(normalized);
        setBackgroundRemovedSvg(removedNormalized);
        setHasDetectedBackground(true);
        setUseBackgroundRemoved(true);
        setDetectionReason(detection.reason);

        // Use the background-removed version by default
        setFormData(prev => ({ ...prev, icon_svg: removedNormalized, icon_type: 'svg' }));

        toast.info('Background detected in icon. Compare both versions below.', { duration: 4000 });
      } else {
        // No background - normal flow
        clearBackgroundDetection();
        setFormData(prev => ({ ...prev, icon_svg: normalized, icon_type: 'svg' }));

        if (result.normalized) {
          toast.success('Icon uploaded and optimized for dynamic colors');
        }
      }
    } catch {
      toast.error('Failed to read SVG file');
    }
  };

  // Handle SVG file upload from input
  const handleSVGUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processUploadedFile(file);
    e.target.value = ''; // Reset input
  };

  // Preview config
  const previewConfig: CategoryIconConfig = {
    icon_type: formData.icon_type,
    abbreviation:
      formData.abbreviation ||
      (isSubcategory ? formData.subcategory : formData.category)
        .split(/[\s_-]+/)
        .slice(0, 3)
        .map(w => w[0]?.toUpperCase() || '')
        .join(''),
    icon_shape: formData.icon_shape,
    fill_color: formData.fill_color,
    border_color: formData.border_color,
    text_color: formData.text_color,
    icon_svg: formData.icon_svg,
  };

  const domainLabel = domain === 'lv_products' ? 'LV/AV Products' : 'Lighting Fixtures';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Create New {isSubcategory ? 'Subcategory' : 'Category'}</DialogTitle>
          <DialogDescription>
            Add a new custom {isSubcategory ? 'subcategory' : 'category'} for {domainLabel}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-1 space-y-4 py-4">
          {/* Parent Category Selection (for subcategories) */}
          {isSubcategory && parentCategories.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="parent_category" className="text-sm font-medium">
                Parent Category *
              </Label>
              <Select
                value={formData.category}
                onValueChange={value => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger className="border-border/50 focus:border-primary/50">
                  <SelectValue placeholder="Select parent category" />
                </SelectTrigger>
                <SelectContent>
                  {parentCategories.map(cat => (
                    <SelectItem key={cat.id} value={cat.category}>
                      <div className="flex items-center gap-2">
                        <CategoryIcon category={cat} size={16} />
                        <span>{cat.display_name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Category Name (for parent categories) */}
          {!isSubcategory && (
            <div className="space-y-2">
              <Label htmlFor="category" className="text-sm font-medium">
                Category Name *
              </Label>
              <Input
                id="category"
                className="border-border/50 focus-visible:border-primary/50"
                value={formData.category}
                onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
                placeholder="e.g., Network Equipment"
              />
            </div>
          )}

          {/* Subcategory Name */}
          {isSubcategory && (
            <div className="space-y-2">
              <Label htmlFor="subcategory" className="text-sm font-medium">
                Subcategory Name *
              </Label>
              <Input
                id="subcategory"
                className="border-border/50 focus-visible:border-primary/50"
                value={formData.subcategory}
                onChange={e => setFormData(prev => ({ ...prev, subcategory: e.target.value }))}
                placeholder="e.g., Access Points"
              />
            </div>
          )}

          {/* Display Name */}
          <div className="space-y-2">
            <Label htmlFor="display_name" className="text-sm font-medium">
              Display Name
            </Label>
            <Input
              id="display_name"
              className="border-border/50 focus-visible:border-primary/50"
              value={formData.display_name}
              onChange={e => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
              placeholder={
                isSubcategory ? formData.subcategory : formData.category || 'Display name'
              }
            />
            <p className="text-xs text-muted-foreground">
              Leave blank to use the {isSubcategory ? 'subcategory' : 'category'} name
            </p>
          </div>

          <Separator />

          {/* Icon Type Toggle */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Icon Type</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={formData.icon_type === 'abbreviation' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFormData(prev => ({ ...prev, icon_type: 'abbreviation' }))}
              >
                Text Icon
              </Button>
              <Button
                type="button"
                variant={formData.icon_type === 'svg' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFormData(prev => ({ ...prev, icon_type: 'svg' }))}
              >
                Upload SVG
              </Button>
            </div>
          </div>

          {/* SVG Upload Section */}
          {formData.icon_type === 'svg' && (
            <div className="space-y-3">
              {/* Drag and Drop Zone */}
              {!formData.icon_svg ? (
                <div
                  className={cn(
                    'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors duration-200',
                    isDragging
                      ? 'border-primary bg-primary/5'
                      : 'border-muted-foreground/25 hover:border-primary/50',
                  )}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('svg-upload')?.click()}
                >
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm font-medium">Click or drag SVG file to upload</p>
                  <p className="text-xs text-muted-foreground mt-1">SVG files only</p>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-muted rounded-lg">
                    <CategoryIcon category={previewConfig} size={40} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-600">SVG uploaded</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, icon_svg: null }));
                      clearBackgroundDetection();
                      setSvgValidationWarnings([]);
                    }}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                </div>
              )}

              <input
                id="svg-upload"
                type="file"
                accept=".svg"
                className="hidden"
                onChange={handleSVGUpload}
              />

              {/* Color Selection for SVG */}
              {formData.icon_svg && (
                <>
                  <div className="space-y-2">
                    <Label className="text-sm">Color Preset</Label>
                    <div className="flex flex-wrap gap-2">
                      {COLOR_PRESETS.map(preset => (
                        <button
                          key={preset.name}
                          type="button"
                          className={cn(
                            'w-7 h-7 rounded border-2 transition-all',
                            formData.fill_color === preset.fill
                              ? 'ring-2 ring-primary ring-offset-2'
                              : 'hover:scale-110',
                          )}
                          style={{ backgroundColor: preset.fill, borderColor: preset.border }}
                          onClick={() =>
                            setFormData(prev => ({
                              ...prev,
                              fill_color: preset.fill,
                              border_color: preset.border,
                            }))
                          }
                          title={preset.name}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Custom Color */}
                  <div className="space-y-1">
                    <Label htmlFor="svg_fill_color" className="text-xs font-medium">
                      Custom Color
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        id="svg_fill_color"
                        value={formData.fill_color}
                        onChange={e =>
                          setFormData(prev => ({ ...prev, fill_color: e.target.value }))
                        }
                        className="w-10 h-8 p-1 cursor-pointer border-border/50"
                      />
                      <Input
                        value={formData.fill_color}
                        onChange={e =>
                          setFormData(prev => ({ ...prev, fill_color: e.target.value }))
                        }
                        className="flex-1 text-xs h-8 border-border/50 focus-visible:border-primary/50"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Background Comparison UI */}
              {hasDetectedBackground && originalSvg && backgroundRemovedSvg && (
                <div className="border rounded-lg p-3 bg-muted/30">
                  <div className="flex items-center gap-2 mb-3 text-sm text-amber-600">
                    <AlertCircle className="h-4 w-4" />
                    <span>Background detected - choose your preferred version</span>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Original version */}
                    <label className="cursor-pointer">
                      <div
                        className={cn(
                          'border-2 rounded-lg p-3 transition-all',
                          !useBackgroundRemoved ? 'border-primary bg-primary/5' : 'border-muted',
                        )}
                      >
                        <div className="w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                          <div
                            style={{ color: formData.fill_color }}
                            dangerouslySetInnerHTML={{
                              __html: sanitizeSVG(constrainSvgForPreview(originalSvg, 40)),
                            }}
                          />
                        </div>
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="radio"
                            name="bgChoice"
                            checked={!useBackgroundRemoved}
                            onChange={() => handleBackgroundChoice(false)}
                            className="accent-primary"
                          />
                          <span className="text-xs font-medium">Original</span>
                        </div>
                      </div>
                    </label>

                    {/* Background removed version */}
                    <label className="cursor-pointer">
                      <div
                        className={cn(
                          'border-2 rounded-lg p-3 transition-all',
                          useBackgroundRemoved ? 'border-primary bg-primary/5' : 'border-muted',
                        )}
                      >
                        <div className="w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                          <div
                            style={{ color: formData.fill_color }}
                            dangerouslySetInnerHTML={{
                              __html: sanitizeSVG(constrainSvgForPreview(backgroundRemovedSvg, 40)),
                            }}
                          />
                        </div>
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="radio"
                            name="bgChoice"
                            checked={useBackgroundRemoved}
                            onChange={() => handleBackgroundChoice(true)}
                            className="accent-primary"
                          />
                          <span className="text-xs font-medium">Background Removed</span>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Abbreviation Icon Options */}
          {formData.icon_type === 'abbreviation' && (
            <>
              {/* Preview, Abbreviation and Shape */}
              <div className="flex items-center gap-4">
                <div>
                  <Label className="text-sm">Preview</Label>
                  <div className="mt-2 p-3 bg-muted rounded-lg">
                    <CategoryIcon category={previewConfig} size={40} />
                  </div>
                </div>

                <div className="flex-1 space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="abbreviation" className="text-sm font-medium">
                      Icon Text
                    </Label>
                    <Input
                      id="abbreviation"
                      className="border-border/50 focus-visible:border-primary/50"
                      value={formData.abbreviation}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          abbreviation: e.target.value.toUpperCase().slice(0, 4),
                        }))
                      }
                      placeholder={
                        (isSubcategory ? formData.subcategory : formData.category)
                          .split(/[\s_-]+/)
                          .slice(0, 3)
                          .map(w => w[0]?.toUpperCase() || '')
                          .join('') || 'ABC'
                      }
                      maxLength={4}
                    />
                    <p className="text-xs text-muted-foreground">
                      1-4 characters (auto-generated if empty)
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium">Shape</Label>
                    <Select
                      value={formData.icon_shape}
                      onValueChange={(value: 'square' | 'circle') =>
                        setFormData(prev => ({ ...prev, icon_shape: value }))
                      }
                    >
                      <SelectTrigger className="border-border/50 focus:border-primary/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="square">Square</SelectItem>
                        <SelectItem value="circle">Circle</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Color Presets */}
              <div className="space-y-2">
                <Label className="text-sm">Color Preset</Label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_PRESETS.map(preset => (
                    <button
                      key={preset.name}
                      type="button"
                      className={cn(
                        'w-7 h-7 rounded border-2 transition-all',
                        formData.fill_color === preset.fill
                          ? 'ring-2 ring-primary ring-offset-2'
                          : 'hover:scale-110',
                      )}
                      style={{ backgroundColor: preset.fill, borderColor: preset.border }}
                      onClick={() =>
                        setFormData(prev => ({
                          ...prev,
                          fill_color: preset.fill,
                          border_color: preset.border,
                        }))
                      }
                      title={preset.name}
                    />
                  ))}
                </div>
              </div>

              {/* Custom Colors */}
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="fill_color" className="text-xs font-medium">
                    Fill
                  </Label>
                  <div className="flex gap-1">
                    <Input
                      type="color"
                      id="fill_color"
                      value={formData.fill_color}
                      onChange={e => setFormData(prev => ({ ...prev, fill_color: e.target.value }))}
                      className="w-10 h-8 p-1 cursor-pointer border-border/50"
                    />
                    <Input
                      value={formData.fill_color}
                      onChange={e => setFormData(prev => ({ ...prev, fill_color: e.target.value }))}
                      className="flex-1 text-xs h-8 border-border/50 focus-visible:border-primary/50"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="border_color" className="text-xs font-medium">
                    Border
                  </Label>
                  <div className="flex gap-1">
                    <Input
                      type="color"
                      id="border_color"
                      value={formData.border_color}
                      onChange={e =>
                        setFormData(prev => ({ ...prev, border_color: e.target.value }))
                      }
                      className="w-10 h-8 p-1 cursor-pointer border-border/50"
                    />
                    <Input
                      value={formData.border_color}
                      onChange={e =>
                        setFormData(prev => ({ ...prev, border_color: e.target.value }))
                      }
                      className="flex-1 text-xs h-8 border-border/50 focus-visible:border-primary/50"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="text_color" className="text-xs font-medium">
                    Text
                  </Label>
                  <div className="flex gap-1">
                    <Input
                      type="color"
                      id="text_color"
                      value={formData.text_color}
                      onChange={e => setFormData(prev => ({ ...prev, text_color: e.target.value }))}
                      className="w-10 h-8 p-1 cursor-pointer border-border/50"
                    />
                    <Input
                      value={formData.text_color}
                      onChange={e => setFormData(prev => ({ ...prev, text_color: e.target.value }))}
                      className="flex-1 text-xs h-8 border-border/50 focus-visible:border-primary/50"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
